import { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, Send, Loader2, CheckCircle2, Clock, AlertTriangle,
  RefreshCw, ChevronDown, ChevronUp, XCircle, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const issueCategories = [
  { value: 'unable_to_load', label: 'Unable to Load Assessment' },
  { value: 'answers_wiped', label: 'Answers Wiped / Lost Data' },
  { value: 'form_rendering', label: 'Form Display / Rendering Issue' },
  { value: 'login_issue', label: 'Login / Access Issue' },
  { value: 'data_issue', label: 'Incorrect Data / Wrong Info' },
  { value: 'other', label: 'Other' },
];

const statusIcons = {
  reported: { icon: AlertCircle, color: 'text-blue-600 bg-blue-50', label: 'Reported' },
  diagnosing: { icon: Loader2, color: 'text-amber-600 bg-amber-50', label: 'Diagnosing' },
  fix_ready: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50', label: 'Fix Ready' },
  fix_applied: { icon: CheckCircle2, color: 'text-green-600 bg-green-50', label: 'Fixed' },
  escalated: { icon: AlertTriangle, color: 'text-red-600 bg-red-50', label: 'Escalated' },
  resolved: { icon: CheckCircle2, color: 'text-green-700 bg-green-50', label: 'Resolved' },
};

export default function IssueReport({ token }) {
  const [view, setView] = useState('list'); // list | new | detail
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Form state
  const [employeeName, setEmployeeName] = useState('');
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/super-support/issues?page_size=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIssues(data.issues);
      }
    } catch (err) {
      console.error('Fetch issues error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchIssueDetail = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/super-support/issues/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedIssue(await res.json());
        setView('detail');
      }
    } catch (err) {
      toast.error('Failed to load issue details');
    }
  }, [token]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  const handleSubmitIssue = async () => {
    if (!employeeName.trim()) return toast.error('Please enter the affected person/area name');
    if (!category) return toast.error('Please select a category');
    if (!description.trim()) return toast.error('Please describe the issue');

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/super-support/report-issue`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_name: employeeName,
          employee_email: employeeEmail || null,
          issue_category: category,
          issue_description: description
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to submit issue');

      toast.success('Issue reported! AI is analyzing and attempting a fix...');
      setEmployeeName('');
      setEmployeeEmail('');
      setCategory('');
      setDescription('');
      setView('list');
      fetchIssues();

      // Poll for diagnosis result
      setTimeout(() => fetchIssueDetail(data.issueId), 3000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async (issueId) => {
    setRetrying(true);
    try {
      const res = await fetch(`${API_URL}/api/super-support/issues/${issueId}/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Retry triggered. AI is re-analyzing...');
        setTimeout(() => fetchIssueDetail(issueId), 3000);
      }
    } catch (err) {
      toast.error('Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Issue Reports</h2>
        <div className="flex gap-2">
          {view !== 'list' && (
            <Button variant="outline" size="sm" onClick={() => { setView('list'); setSelectedIssue(null); }} data-testid="back-to-list-btn">
              Back to List
            </Button>
          )}
          {view === 'list' && (
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setView('new')} data-testid="new-issue-btn">
              <AlertCircle className="w-4 h-4 mr-1" /> Report Issue
            </Button>
          )}
        </div>
      </div>

      {/* New Issue Form */}
      {view === 'new' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Report a New Issue</CardTitle>
            <p className="text-xs text-slate-500">Describe the issue and our AI will automatically diagnose and attempt to fix it.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Affected Person / Area *</Label>
                <Input
                  value={employeeName}
                  onChange={(e) => setEmployeeName(e.target.value)}
                  placeholder="e.g., John Smith or 'All Jamaica forms'"
                  data-testid="issue-employee-name"
                />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input
                  value={employeeEmail}
                  onChange={(e) => setEmployeeEmail(e.target.value)}
                  placeholder="email@example.com"
                  data-testid="issue-employee-email"
                />
              </div>
            </div>

            <div>
              <Label>Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="issue-category">
                  <SelectValue placeholder="Select issue category" />
                </SelectTrigger>
                <SelectContent>
                  {issueCategories.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Issue Description *</Label>
              <p className="text-xs text-slate-500 mb-1">Be as detailed as possible. Include what happened, what was expected, and any error messages.</p>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue in detail..."
                className="min-h-[180px] text-sm"
                data-testid="issue-description"
              />
            </div>

            <Button
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={submitting}
              onClick={handleSubmitIssue}
              data-testid="submit-issue-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Submit Issue</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Issue Detail */}
      {view === 'detail' && selectedIssue && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedIssue.employeeName}</h3>
                  <p className="text-sm text-slate-500">{selectedIssue.category?.replace(/_/g, ' ')}</p>
                </div>
                {(() => {
                  const s = statusIcons[selectedIssue.status] || statusIcons.reported;
                  return <Badge className={`${s.color} border-0`}><s.icon className="w-3 h-3 mr-1" />{s.label}</Badge>;
                })()}
              </div>
              <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{selectedIssue.description}</p>
            </CardContent>
          </Card>

          {/* Diagnosis Result */}
          {selectedIssue.diagnosisResult && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">AI Diagnosis</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => handleRetry(selectedIssue.id)} disabled={retrying} data-testid="retry-fix-btn">
                    {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                    Retry
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <DiagnosisDisplay diagnosis={selectedIssue.diagnosisResult} />
              </CardContent>
            </Card>
          )}

          {/* Activity Log */}
          {selectedIssue.activityLog?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {selectedIssue.activityLog.map((log, i) => (
                    <div key={log.id || i} className="flex gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-slate-700">{log.action?.replace(/_/g, ' ')}</div>
                        {log.details && typeof log.details === 'object' && (
                          <pre className="text-xs text-slate-500 mt-1 bg-slate-50 rounded p-2 overflow-x-auto max-h-40">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        )}
                        <div className="text-xs text-slate-400 mt-1">
                          {log.performedBy} - {log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Issues List */}
      {view === 'list' && (
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
              </div>
            ) : issues.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                No issues reported yet. Click "Report Issue" to get started.
              </div>
            ) : (
              <div className="divide-y">
                {issues.map((issue) => {
                  const s = statusIcons[issue.status] || statusIcons.reported;
                  return (
                    <div
                      key={issue.id}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => fetchIssueDetail(issue.id)}
                      data-testid={`issue-row-${issue.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-slate-900">{issue.employeeName}</span>
                            <Badge variant="outline" className="text-xs">{issue.category?.replace(/_/g, ' ')}</Badge>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">{issue.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            <span>By: {issue.reportedBy}</span>
                            <span>{issue.createdAt ? new Date(issue.createdAt).toLocaleDateString() : ''}</span>
                          </div>
                        </div>
                        <Badge className={`${s.color} border-0 text-xs ml-3`}>
                          {s.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DiagnosisDisplay({ diagnosis }) {
  const [expanded, setExpanded] = useState(false);

  if (!diagnosis) return null;

  const aiAnalysis = diagnosis.ai_analysis || {};
  const checks = diagnosis.checks || [];

  return (
    <div className="space-y-3">
      {aiAnalysis.root_cause && (
        <div className="bg-slate-50 rounded-lg p-3">
          <Label className="text-xs text-slate-500">Root Cause</Label>
          <p className="text-sm text-slate-800 mt-1">{aiAnalysis.root_cause}</p>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        {aiAnalysis.can_fix !== undefined && (
          <Badge className={aiAnalysis.can_fix ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
            {aiAnalysis.can_fix ? 'Auto-fixable' : 'Needs manual fix'}
          </Badge>
        )}
        {aiAnalysis.confidence && (
          <Badge variant="outline">Confidence: {aiAnalysis.confidence}</Badge>
        )}
        {aiAnalysis.num_fixes > 0 && (
          <Badge variant="outline">{aiAnalysis.num_fixes} code fix(es)</Badge>
        )}
      </div>

      {aiAnalysis.verification && (
        <div className="bg-blue-50 rounded-lg p-3">
          <Label className="text-xs text-blue-600">Verification</Label>
          <p className="text-sm text-blue-800 mt-1">{aiAnalysis.verification}</p>
        </div>
      )}

      {checks.length > 0 && (
        <div>
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {checks.length} diagnostic check(s)
          </button>
          {expanded && (
            <div className="mt-2 space-y-1">
              {checks.map((check, i) => (
                <div key={i} className="text-xs bg-slate-50 rounded p-2">
                  <span className="font-medium">{check.check}</span>: {check.result}
                  {check.files_analyzed && <span className="text-slate-400 ml-2">({check.files_analyzed.length} files)</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
